import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldX } from "lucide-react";
import api from "../api";
import { useLang } from "../i18n";
import FormNatural from "./FormNatural";
import FormJuridica from "./FormJuridica";
import FormDeclaracion from "./FormDeclaracion";
import FormCertificacion from "./FormCertificacion";
import FormCarta from "./FormCarta";
import FormAseguradora from "./FormAseguradora";
import FormAseguradoraPJ from "./FormAseguradoraPJ";
import PublicKye from "./PublicKye";
import PublicKyp from "./PublicKyp";

const COMPONENTS = {
  dd_persona_natural: FormNatural,
  dd_persona_juridica: FormJuridica,
  declaracion_fondos: FormDeclaracion,
  certificacion_beneficiario: FormCertificacion,
  carta_responsabilidad: FormCarta,
  dd_pn_aseguradora: FormAseguradora,
  dd_pj_aseguradora: FormAseguradoraPJ,
  kye_employee: PublicKye,
  kyp_supplier: PublicKyp,
};

export default function PublicForm() {
  const { token } = useParams();
  const { t } = useLang();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/form/${token}`).then((r) => setMeta(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFE] bg-[radial-gradient(ellipse_at_top_right,#EAF1FC,transparent_55%)] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-[#DFE8F6] shadow-[0_20px_50px_rgba(63,69,79,0.08)] p-10 text-center max-w-md animate-[fadeIn_0.5s_ease-out]" data-testid="public-form-invalid">
          <ShieldX className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-[#525B67]">{t("forms.invalid_link")}</p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen bg-[#F8FAFE] flex items-center justify-center" data-testid="public-form-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#044CBC]" />
      </div>
    );
  }

  const Comp = COMPONENTS[meta.form_type];
  return (
    <div className="min-h-screen bg-[#F8FAFE] bg-[radial-gradient(ellipse_at_top_right,#EAF1FC,transparent_55%)]" data-testid="public-form-page">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ backgroundImage: "linear-gradient(#DFE8F6 1px, transparent 1px), linear-gradient(90deg, #DFE8F6 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <header className="relative z-10 bg-white/85 backdrop-blur border-b border-[#DFE8F6] shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-[72px] flex items-center gap-3">
          <div className="bg-white rounded-xl border border-[#DFE8F6] shadow-[0_8px_24px_rgba(63,69,79,0.08)] p-1.5">
            <img src={meta.tenant_logo || "/logo.png"} alt="KYCFederal" className="w-10 h-10 object-contain" data-testid="public-form-logo" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-wide text-sm text-[#16335F]">{t("brand.name")}</div>
            <div className="text-[11px] font-semibold text-[#044CBC]" data-testid="public-form-tenant">{meta.tenant_name}</div>
          </div>
          <span className="ml-auto hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-[#EAF1FC] border border-[#F3D5C8] text-[#033A8C]">
            <ShieldCheck className="w-3 h-3" /> {t("vf.title")}
          </span>
        </div>
      </header>
      <main className="relative z-10 max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 animate-[fadeIn_0.5s_ease-out]">
        {Comp && <Comp publicToken={token} />}
      </main>
      <footer className="relative z-10 flex flex-col items-center gap-2 text-[11px] text-[#8A94A2] pb-8">
        <img src="/iso27001-transparent.png" alt="ISO 27001 Bureau Veritas Certification" className="h-9 w-auto object-contain" data-testid="public-form-iso-logo" />
        <span>KYCFederal v4.0 SSNF 2026</span>
      </footer>
    </div>
  );
}
